import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowLeft, Minus, Plus, Clock, Package, Flame } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCart, API } from '../App';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [fragrances, setFragrances] = useState([]);
  const [selectedFragrance, setSelectedFragrance] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch product
        const response = await fetch(`${API}/products/${id}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          
          // Pre-fill fragrance if product has a fixed fragrance
          if (data.fragrance_type === 'fixed' && data.fixed_fragrance) {
            setSelectedFragrance(data.fixed_fragrance);
          }
          
          // Fetch related products
          const relatedRes = await fetch(`${API}/products?category=${data.category}`);
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            setRelatedProducts(relatedData.filter(p => p.id !== id).slice(0, 4));
          }
        } else {
          navigate('/shop');
        }

        // Fetch fragrances
        const fragrancesRes = await fetch(`${API}/fragrances`);
        if (fragrancesRes.ok) {
          const fragrancesData = await fragrancesRes.json();
          setFragrances(fragrancesData);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/shop');
      }
      setLoading(false);
    };
    
    fetchData();
    window.scrollTo(0, 0);
  }, [id, navigate]);

  const handleAddToCart = async () => {
    // Determine the final fragrance value based on product settings
    let finalFragrance = null;
    
    if (product.fragrance_type === 'fixed') {
      finalFragrance = product.fixed_fragrance;
    } else if (product.fragrance_type === 'selectable') {
      if (!selectedFragrance) {
        toast.error('Please select a fragrance');
        return;
      }
      finalFragrance = selectedFragrance;
    }
    
    await addToCart(product.id, quantity, finalFragrance);
    toast.success(`${product.name} added to cart!`, {
      description: finalFragrance ? `${finalFragrance} × ${quantity}` : `Quantity: ${quantity}`,
      action: {
        label: 'View Cart',
        onClick: () => navigate('/cart')
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#595959] hover:text-[#C5A059] transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Shop</span>
        </button>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#F5F1EB] sticky top-28">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#C5A059] to-[#E5C88A] opacity-30" />
                </div>
              )}
              {product.featured && (
                <div className="absolute top-4 left-4 bg-[#5D6D53] text-white text-sm px-4 py-1.5 rounded-full font-medium">
                  Featured
                </div>
              )}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:py-8"
          >
            <p className="text-sm text-[#8C8C8C] uppercase tracking-wider mb-2">
              {product.category?.replace('-', ' ')}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4" data-testid="product-name">
              {product.name}
            </h1>
            
            {product.scent && (
              <p className="text-[#595959] text-lg mb-4 flex items-center gap-2">
                <Flame className="w-5 h-5 text-[#C5A059]" />
                {product.scent}
              </p>
            )}

            <p className="text-3xl text-[#C5A059] font-serif mb-8" data-testid="product-price">
              £{product.price?.toFixed(2)}
            </p>

            <p className="text-[#595959] leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Product Details */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {product.burn_time && (
                <div className="flex items-center gap-3 text-[#595959]">
                  <Clock className="w-5 h-5 text-[#C5A059]" />
                  <span>Burn time: {product.burn_time}</span>
                </div>
              )}
              {product.weight && (
                <div className="flex items-center gap-3 text-[#595959]">
                  <Package className="w-5 h-5 text-[#C5A059]" />
                  <span>Weight: {product.weight}</span>
                </div>
              )}
            </div>

            {/* Fragrance Selection - Based on product fragrance_type */}
            {product.fragrance_type === 'fixed' && product.fixed_fragrance && (
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Fragrance
                </label>
                <div className="flex items-center gap-2 p-3 bg-[#F5F1EB] rounded-lg border border-stone-200">
                  <Flame className="w-5 h-5 text-[#C5A059]" />
                  <span className="font-medium text-[#2C2C2C]">{product.fixed_fragrance}</span>
                  <span className="text-xs text-[#8C8C8C] ml-2">(This product's fragrance)</span>
                </div>
              </div>
            )}
            
            {product.fragrance_type === 'selectable' && (
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Select Fragrance *
                </label>
                <Select value={selectedFragrance} onValueChange={setSelectedFragrance}>
                  <SelectTrigger className="w-full md:w-72 input-glow" data-testid="fragrance-select">
                    <SelectValue placeholder="Choose your fragrance..." />
                  </SelectTrigger>
                  <SelectContent>
                    {fragrances.map((fragrance) => (
                      <SelectItem key={fragrance.id} value={fragrance.name}>
                        {fragrance.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 hover:bg-stone-100 transition-colors"
                  data-testid="decrease-qty"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-6 py-3 font-medium min-w-[60px] text-center" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-3 hover:bg-stone-100 transition-colors"
                  data-testid="increase-qty"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={cartLoading || product.stock === 0}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
            </div>

            {/* Stock Status */}
            {product.stock > 0 && product.stock <= 5 && (
              <p className="text-sm text-[#D48C70] mb-4">
                Only {product.stock} left in stock!
              </p>
            )}

            {/* Features */}
            <div className="border-t border-stone-200 pt-8 mt-8">
              <div className="grid grid-cols-2 gap-4 text-sm text-[#595959]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#5D6D53]" />
                  Hand-poured in Scotland
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#5D6D53]" />
                  100% Soy Wax
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#5D6D53]" />
                  Eco-friendly packaging
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#5D6D53]" />
                  Premium fragrance oils
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24">
            <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((relProduct, index) => (
                <motion.div
                  key={relProduct.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProductCard product={relProduct} />
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Mobile Sticky Add to Cart */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-4 lg:hidden z-40">
        <div className="flex items-center gap-4">
          <div>
            <p className="font-serif text-lg">{product.name}</p>
            <p className="text-[#C5A059] font-semibold">£{product.price?.toFixed(2)}</p>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={cartLoading || product.stock === 0}
            className="btn-primary flex-1"
            data-testid="mobile-add-to-cart-btn"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
